/** @jsx jsx */
import React, { useState, useEffect, useRef } from 'react';
import { jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import {
  Button,
  Input,
  Select,
  DatePicker,
  Modal,
  Form,
  message,
  Icon,
  Tooltip,
  Table,
} from 'antd';
import moment from 'moment';
import { get, uniqBy } from 'lodash';

import { exportToCsv, getConfig, userHasRole } from '../../actions/utils';
import { getLicenseNumbers } from '../../_shared/services/api.service';
import {
  getOperationsLog,
  createOperationsLogEntry,
  updateOperationsLogEntry,
  deleteOperationsLogEntry,
  importOperationsLogEntries,
  getOperationsLogOrderIds,
} from '../../_shared/services/operationslog.service';
import styles, { priorityColor } from './styles';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const PRIORITIES = ['Low', 'Medium', 'High'];
const TAGS = [
  'Compliance',
  'Customer Request',
  'Driver',
  'Internal Note',
  'Issue',
  'Missing Docs',
  'Missing Invoice',
  'Missing Packages',
  'Other',
  'Rejection',
  'Scheduling',
];
// Card #256 — tags offered by the FILTER dropdown only. 'Override' rows are
// written exclusively by the server COD-override audit (operationslog.js
// logPaymentOverride, tag 'Override'); keeping it out of TAGS stops manual
// entries from claiming that tag while still letting admins filter to it.
const FILTER_TAGS = [...TAGS, 'Override'].sort();
// Card #256 round 7 — the 'Override' filter option exists ONLY because of the
// payment-requirement feature, so the master switch
// (`orders.paymentRequirementEnabled`, default OFF) takes it back out of the
// dropdown. This removes a filter OPTION, never data: any log row already
// tagged 'Override' still renders normally in the table, is still exported by
// Export CSV, and is still reachable via the free-text Search box.
//
// NOT computed at module scope: `getConfig` reads `window.APPCONFIG`, which is
// only populated after the app fetches its config, so a module-load read would
// always see the `false` fallback and hide the option even when it is on.
const filterTagOptions = () => (
  getConfig('orders.paymentRequirementEnabled', false)
    ? FILTER_TAGS
    : FILTER_TAGS.filter(t => t !== 'Override')
);

// Default date range: current week + 7 days into the past
const DEFAULT_RANGE = () => [
  moment().subtract(7, 'days').startOf('day'),
  moment().endOf('week'),
];

const DEFAULT_FILTERS = () => ({
  dateRange: DEFAULT_RANGE(),
  priority: undefined,
  tag: undefined,
  orderId: '',
  licenseNumber: '',
  // QA: default to "All" (no filter)
  completed: 'all',
  search: '',
});

const PriorityPill = ({ value }) => {
  const style = priorityColor(value);
  return (
    <span css={styles.priorityPill} style={style}>{value || '—'}</span>
  );
};

const TagPill = ({ value }) => {
  if (!value) { return <span>—</span>; }
  return <span css={styles.tagPill}>{value}</span>;
};

const Avatar = ({ initials, name, date }) => {
  if (!initials) { return <span>—</span>; }
  const tip = (
    <div>
      <div style={{ fontWeight: 600 }}>{name}</div>
      {date && <div style={{ fontSize: 11, opacity: 0.8 }}>{moment(date).format('MMM D, YYYY h:mm A')}</div>}
    </div>
  );
  return (
    <Tooltip title={tip}>
      <span css={styles.avatar}>{initials}</span>
    </Tooltip>
  );
};

const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i++; }
        else { inQuotes = false; }
      } else { cur += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === ',') { row.push(cur); cur = ''; }
      else if (ch === '\n' || ch === '\r') {
        if (cur !== '' || row.length > 0) { row.push(cur); rows.push(row); row = []; cur = ''; }
        if (ch === '\r' && text[i + 1] === '\n') { i++; }
      } else { cur += ch; }
    }
  }
  if (cur !== '' || row.length > 0) { row.push(cur); rows.push(row); }
  return rows.filter(r => r.some(c => c !== ''));
};

// Operations Log CSV dates arrive in several shapes: the Export in this file
// writes `YYYY-MM-DD HH:mm`, but source spreadsheets commonly use `M/D/YY H:mm`
// and similar. Parse tolerantly and ALWAYS return an ISO string — the list
// endpoint range-filters `entryDate` as an ISO string comparison, so a raw
// value like "6/30/26 20:38" sorts after the ISO upper bound and the row
// silently disappears from the results. Returns '' when empty/unparseable so
// the caller can apply its own default.
const CSV_DATE_FORMATS = [
  'M/D/YY H:mm', 'M/D/YYYY H:mm', 'M/D/YY', 'M/D/YYYY',
  'YYYY-MM-DD HH:mm', 'YYYY-MM-DD', moment.ISO_8601,
];

const parseCsvDate = (raw) => {
  const val = (raw || '').trim();
  if (!val) { return ''; }
  const strict = moment(val, CSV_DATE_FORMATS, true);
  const parsed = strict.isValid() ? strict : moment(val);
  return parsed.isValid() ? parsed.toISOString() : '';
};

const csvRowsToEntries = (rows) => {
  if (!rows || rows.length === 0) { return []; }
  const header = rows[0].map(h => (h || '').trim().toLowerCase());
  const colIdx = (name) => header.indexOf(name);
  const entries = [];
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    const get1 = (name) => {
      const idx = colIdx(name);
      return idx >= 0 ? (r[idx] || '').trim() : '';
    };
    entries.push({
      priority: get1('priority'),
      entryDate: parseCsvDate(get1('date')) || moment().toISOString(),
      orderId: get1('order id') || get1('order/order id'),
      licenseNumber: get1('license') || get1('license number'),
      licenseDba: get1('dba') || get1('dba name'),
      description: get1('description'),
      tag: get1('tag'),
      actionTaken: get1('action taken'),
      followUpAction: get1('follow up action') || get1('follow up'),
      completed: ['y', 'yes', 'true', '1'].includes((get1('completed') || '').toLowerCase()),
    });
  }
  return entries;
};

// AntD Form-compatible License select: receives `value`/`onChange` from the
// Form, and additionally calls `onPick` with the picked license's DBA so the
// parent can auto-fill the DBA field.
const LicenseSelectInner = ({
  licenses, value, onChange, onPick, loading, disabled, placeholder,
}) => {
  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={loading}
      placeholder={placeholder || 'Type to filter licenses...'}
      value={value || undefined}
      onChange={(v) => {
        if (onChange) { onChange(v || ''); }
        if (!v) { if (onPick) { onPick({ licenseNumber: '', licenseDba: '' }); } return; }
        const found = licenses.find(l => get(l, 'License #') === v);
        const filledDba = get(found, 'DBA') || get(found, 'Licensee') || '';
        if (onPick) { onPick({ licenseNumber: v, licenseDba: filledDba }); }
      }}
      filterOption={(input, option) => {
        const opt = option && option.props ? option.props : {};
        const searchable = `${opt.value || ''} ${opt.dba || ''} ${opt.licensee || ''}`.toLowerCase();
        return searchable.indexOf((input || '').toLowerCase()) >= 0;
      }}
      style={{ width: '100%' }}
      optionLabelProp="label"
    >
      {licenses.map(l => {
        const ln = get(l, 'License #');
        const dbaName = get(l, 'DBA') || '';
        const licensee = get(l, 'Licensee') || '';
        const display = dbaName || licensee || ln;
        return (
          <Option
            key={ln}
            value={ln}
            label={`${ln}${display ? ` — ${display}` : ''}`}
            dba={dbaName}
            licensee={licensee}
          >
            <div>
              <div style={{ fontWeight: 600 }}>{ln}</div>
              {display && <div style={{ fontSize: 11, color: '#888' }}>{display}</div>}
            </div>
          </Option>
        );
      })}
    </Select>
  );
};

// AntD Form-compatible Order ID select: searchable dropdown of existing order
// IDs that also lets the user type a custom value (free text).
const OrderIdSelectInner = ({
  orderIds, value, onChange, loading, placeholder,
}) => {
  return (
    <Select
      mode="combobox"
      showSearch
      allowClear
      loading={loading}
      placeholder={placeholder || 'Search Order ID'}
      value={value || undefined}
      onChange={(v) => { if (onChange) { onChange(v || ''); } }}
      filterOption={(input, option) => {
        const v = String(get(option, 'props.value', '')).toLowerCase();
        return v.indexOf((input || '').toLowerCase()) >= 0;
      }}
      style={{ width: '100%' }}
    >
      {orderIds.map(o => (
        <Option key={o} value={o}>{o}</Option>
      ))}
    </Select>
  );
};

const EntryModalInner = ({
  form, entry, onCancel, onSave, saving, licenses, licensesLoading, orderIds, orderIdsLoading,
}) => {
  const { getFieldDecorator, setFieldsValue } = form;
  return (
    <Form layout="vertical">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Form.Item label="Priority">
          {getFieldDecorator('priority', { initialValue: get(entry, 'priority') || undefined })(
            <Select placeholder="Select priority" allowClear>
              {PRIORITIES.map(p => <Option key={p} value={p}>{p}</Option>)}
            </Select>
          )}
        </Form.Item>
        <Form.Item label="Date" required>
          {getFieldDecorator('entryDate', {
            initialValue: entry && entry.entryDate ? moment(entry.entryDate) : moment(),
            rules: [{ required: true, message: 'Date is required' }],
          })(<DatePicker showTime format="MMM D, YYYY h:mm A" style={{ width: '100%' }} />)}
        </Form.Item>
        <Form.Item label="Order / Order ID">
          {getFieldDecorator('orderId', { initialValue: get(entry, 'orderId') })(
            <OrderIdSelectInner
              orderIds={orderIds}
              loading={orderIdsLoading}
              placeholder="Search or enter Order ID"
            />
          )}
        </Form.Item>
        <Form.Item label="Tag" required>
          {getFieldDecorator('tag', {
            initialValue: get(entry, 'tag') || undefined,
            rules: [{ required: true, message: 'Tag is required' }],
          })(
            <Select placeholder="Select tag" showSearch>
              {TAGS.map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          )}
        </Form.Item>
        <Form.Item label="Customer License #">
          {getFieldDecorator('licenseNumber', { initialValue: get(entry, 'licenseNumber') })(
            <LicenseSelectInner
              licenses={licenses}
              loading={licensesLoading}
              onPick={({ licenseDba }) => {
                // Auto-fill DBA when a license is picked.
                setFieldsValue({ licenseDba });
              }}
            />
          )}
        </Form.Item>
        <Form.Item label="DBA Name (auto-filled)">
          {getFieldDecorator('licenseDba', { initialValue: get(entry, 'licenseDba') })(
            <Input placeholder="Auto-fills from selected license" disabled />
          )}
        </Form.Item>
      </div>
      <Form.Item label="Description" required>
        {getFieldDecorator('description', {
          initialValue: get(entry, 'description'),
          rules: [{ required: true, message: 'Description is required' }],
        })(<TextArea rows={2} placeholder="What happened?" />)}
      </Form.Item>
      <Form.Item label="Action Taken">
        {getFieldDecorator('actionTaken', { initialValue: get(entry, 'actionTaken') })(
          <TextArea rows={2} placeholder="What was done?" />
        )}
      </Form.Item>
      <Form.Item label="Follow Up Action (if needed)">
        {getFieldDecorator('followUpAction', { initialValue: get(entry, 'followUpAction') })(
          <TextArea rows={2} placeholder="What still needs to happen?" />
        )}
      </Form.Item>
      <Form.Item label="Completed">
        {getFieldDecorator('completed', { initialValue: !!get(entry, 'completed') })(
          <Select style={{ width: 120 }}>
            <Option value={false}>N</Option>
            <Option value={true}>Y</Option>
          </Select>
        )}
      </Form.Item>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="primary" loading={saving} onClick={() => {
          form.validateFields((err, values) => {
            if (err) { return; }
            onSave({
              ...entry,
              ...values,
              entryDate: values.entryDate ? values.entryDate.toISOString() : moment().toISOString(),
            });
          });
        }}>Save</Button>
      </div>
    </Form>
  );
};

const EntryModal = Form.create({ name: 'opslog-entry' })(EntryModalInner);

export const OperationsLog = () => {
  const user = useSelector(state => get(state, 'user'));
  const isSuperAdmin = userHasRole(0, { user });
  const isAdminLike = userHasRole([0, 1], { user });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => DEFAULT_FILTERS());
  const [pendingFilters, setPendingFilters] = useState(() => DEFAULT_FILTERS());
  const [modalEntry, setModalEntry] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [licensesLoading, setLicensesLoading] = useState(false);
  const [orderIds, setOrderIds] = useState([]);
  const [orderIdsLoading, setOrderIdsLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchData = async (f = filters) => {
    setLoading(true);
    try {
      const params = {
        dateFrom: f.dateRange && f.dateRange[0] ? f.dateRange[0].toISOString() : undefined,
        dateTo: f.dateRange && f.dateRange[1] ? f.dateRange[1].toISOString() : undefined,
        priority: f.priority,
        tag: f.tag,
        orderId: f.orderId,
        licenseNumber: f.licenseNumber,
        completed: f.completed,
        search: f.search,
      };
      const rows = await getOperationsLog(params);
      setData(rows);
    } catch (err) {
      console.error(err);
      message.error('Failed to load operations log');
    } finally {
      setLoading(false);
    }
  };

  const fetchLicenses = async () => {
    setLicensesLoading(true);
    try {
      const all = await getLicenseNumbers();
      const cleaned = uniqBy(
        (all || []).filter(l => !!get(l, 'License #')),
        'License #'
      );
      setLicenses(cleaned);
    } catch (err) {
      console.error('Failed loading licenses', err);
    } finally {
      setLicensesLoading(false);
    }
  };

  const fetchOrderIds = async () => {
    setOrderIdsLoading(true);
    try {
      const ids = await getOperationsLogOrderIds();
      setOrderIds(ids || []);
    } catch (err) {
      console.error('Failed loading order ids', err);
    } finally {
      setOrderIdsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filters);
    fetchLicenses();
    fetchOrderIds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onApply = () => {
    setFilters(pendingFilters);
    fetchData(pendingFilters);
  };

  const onClear = () => {
    const def = DEFAULT_FILTERS();
    setPendingFilters(def);
    setFilters(def);
    fetchData(def);
  };

  const onNew = () => {
    setModalEntry({});
    setModalOpen(true);
  };

  const onEdit = (record) => {
    setModalEntry(record);
    setModalOpen(true);
  };

  const onSave = async (entry) => {
    setSaving(true);
    try {
      if (entry.id) {
        await updateOperationsLogEntry(entry);
        message.success('Entry updated');
      } else {
        await createOperationsLogEntry(entry);
        message.success('Entry created');
      }
      setModalOpen(false);
      setModalEntry(null);
      fetchData();
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.error', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (record) => setConfirmDelete(record);
  const onDelete = async () => {
    if (!confirmDelete) { return; }
    setDeleting(true);
    try {
      await deleteOperationsLogEntry(confirmDelete);
      message.success('Entry deleted');
      setConfirmDelete(null);
      fetchData();
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.error', 'Delete failed'));
    } finally {
      setDeleting(false);
    }
  };

  const onExport = () => {
    const header = [
      'Priority', 'Date', 'Order ID', 'License Number', 'DBA Name',
      'Description', 'Tag', 'Action Taken', 'Follow Up Action',
      'Completed', 'Entered By',
    ];
    const rows = [header, ...data.map(r => [
      r.priority || '',
      r.entryDate ? moment(r.entryDate).format('YYYY-MM-DD HH:mm') : '',
      r.orderId || '',
      r.licenseNumber || '',
      r.licenseDba || '',
      r.description || '',
      r.tag || '',
      r.actionTaken || '',
      r.followUpAction || '',
      r.completed ? 'Y' : 'N',
      r.createdByName || '',
    ])];
    exportToCsv(`operations-log-${moment().format('YYYYMMDD')}.csv`, rows);
  };

  const onImportClick = () => {
    if (fileInputRef.current) { fileInputRef.current.click(); }
  };

  const onImportFile = async (e) => {
    // React 16 pools SyntheticEvents and nulls their fields (including target)
    // once the handler yields at the first await, so capture the input node now
    // — reading e.target in the finally below would otherwise throw on null.
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) { return; }
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      const entries = csvRowsToEntries(rows);
      if (entries.length === 0) {
        message.warning('CSV had no valid rows');
        return;
      }
      const result = await importOperationsLogEntries(entries);
      const inserted = get(result, 'inserted', 0);
      const errors = get(result, 'errors', []);
      if (errors.length > 0) {
        Modal.warning({
          title: `Imported ${inserted} entries — ${errors.length} skipped`,
          content: (
            <div style={{ maxHeight: 280, overflowY: 'auto' }}>
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {errors.map((er, i) => (
                  <li key={i} style={{ fontSize: 12 }}>
                    Row {get(er, 'row', '?')}: {get(er, 'error', 'Unknown error')}
                  </li>
                ))}
              </ul>
            </div>
          ),
        });
      } else {
        message.success(`Imported ${inserted} entries`);
      }
      fetchData();
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.error', 'Import failed'));
    } finally {
      input.value = '';
    }
  };

  const columns = [
    {
      title: 'Priority',
      dataIndex: 'priority',
      width: 90,
      render: v => <PriorityPill value={v} />,
    },
    {
      title: 'Date',
      dataIndex: 'entryDate',
      width: 130,
      render: v => v ? (
        <div>
          <div>{moment(v).format('MMM D, YYYY')}</div>
          <div style={{ fontSize: 11, color: '#888' }}>{moment(v).format('h:mm A')}</div>
        </div>
      ) : '—',
    },
    {
      title: 'Order / Order ID',
      dataIndex: 'orderId',
      width: 130,
      render: v => v || '—',
    },
    {
      title: <div>License<div style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>(Customer / DBA)</div></div>,
      key: 'license',
      width: 160,
      render: (_t, r) => (
        <div>
          <div>{r.licenseNumber || '—'}</div>
          {r.licenseDba && <div style={{ fontSize: 11, color: '#888' }}>{r.licenseDba}</div>}
        </div>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 180,
    },
    {
      title: 'Tag',
      dataIndex: 'tag',
      width: 110,
      render: v => <TagPill value={v} />,
    },
    {
      title: 'Action Taken',
      dataIndex: 'actionTaken',
      width: 160,
    },
    {
      title: <div>Follow Up Action<div style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>(if needed)</div></div>,
      dataIndex: 'followUpAction',
      width: 160,
    },
    {
      title: <div>Completed<div style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>(Y/N)</div></div>,
      dataIndex: 'completed',
      width: 90,
      render: v => <span css={v ? styles.completedY : styles.completedN}>{v ? 'Y' : 'N'}</span>,
    },
    {
      title: 'Entered By',
      key: 'enteredBy',
      width: 90,
      render: (_t, r) => (
        <Avatar
          initials={r.createdByInitials}
          name={r.createdByName}
          date={r.created}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'rowActions',
      width: 100,
      render: (_t, r) => (
        <div>
          <Icon
            type="edit"
            css={styles.iconBtn}
            onClick={() => onEdit(r)}
          />
          {isAdminLike && (
            <Icon
              type="delete"
              css={styles.iconBtnDanger}
              onClick={() => askDelete(r)}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div css={styles.wrapper}>
      <div css={styles.titleBlock}>
        <h2 css={styles.title}>Operations Log</h2>
        <p css={styles.subtitle}>
          Track and manage operational issues, actions, and follow ups. Entries can be tied to orders or used as internal team notes.
        </p>
      </div>

      <div css={styles.headerRow}>
        <div css={styles.topActions}>
          <Tooltip title="Refresh (keeps current filters)">
            <Button onClick={() => fetchData()} icon="reload" loading={loading}>
              Refresh
            </Button>
          </Tooltip>
          {isAdminLike && (
            <Button onClick={onExport} icon="download">Export CSV</Button>
          )}
          {isSuperAdmin && (
            <Button onClick={onImportClick} icon="upload">Import CSV</Button>
          )}
          <Button type="primary" onClick={onNew} icon="plus">New Log Entry</Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={onImportFile}
          />
        </div>
        {isSuperAdmin && <div css={styles.superAdminHint}>Super Admins Only (Import)</div>}
      </div>

      <div css={styles.filterBar}>
        <div css={styles.filterGrid}>
          <div css={styles.dateRangeCol}>
            <label css={styles.filterLabel}>Date Range</label>
            <RangePicker
              value={pendingFilters.dateRange}
              onChange={(v) => setPendingFilters({ ...pendingFilters, dateRange: v })}
              format="MMM D, YYYY"
              style={{ width: '100%' }}
            />
            <div css={styles.filterHint}>(Current week + 7 days back)</div>
          </div>
          <div>
            <label css={styles.filterLabel}>Priority</label>
            <Select
              placeholder="All"
              value={pendingFilters.priority}
              onChange={(v) => setPendingFilters({ ...pendingFilters, priority: v })}
              allowClear
              style={{ width: '100%' }}
            >
              {PRIORITIES.map(p => <Option key={p} value={p}>{p}</Option>)}
            </Select>
          </div>
          <div>
            <label css={styles.filterLabel}>Tag</label>
            <Select
              placeholder="All"
              value={pendingFilters.tag}
              onChange={(v) => setPendingFilters({ ...pendingFilters, tag: v })}
              allowClear
              showSearch
              style={{ width: '100%' }}
            >
              {filterTagOptions().map(t => <Option key={t} value={t}>{t}</Option>)}
            </Select>
          </div>
          <div>
            <label css={styles.filterLabel}>Order ID</label>
            <OrderIdSelectInner
              orderIds={orderIds}
              loading={orderIdsLoading}
              value={pendingFilters.orderId}
              onChange={(v) => setPendingFilters({ ...pendingFilters, orderId: v })}
              placeholder="Search Order ID"
            />
          </div>
          <div>
            <label css={styles.filterLabel}>License Number</label>
            <LicenseSelectInner
              licenses={licenses}
              loading={licensesLoading}
              value={pendingFilters.licenseNumber}
              onChange={(v) => setPendingFilters({ ...pendingFilters, licenseNumber: v })}
              placeholder="Search License"
            />
          </div>
          <div>
            <label css={styles.filterLabel}>Completed (Y/N)</label>
            <Select
              value={pendingFilters.completed}
              onChange={(v) => setPendingFilters({ ...pendingFilters, completed: v })}
              style={{ width: '100%' }}
            >
              <Option value="all">All</Option>
              <Option value="true">Y</Option>
              <Option value="false">N</Option>
            </Select>
          </div>
          <div>
            <label css={styles.filterLabel}>Search</label>
            <Input
              placeholder="Search description, action, order, license, DBA or author..."
              value={pendingFilters.search}
              onChange={(e) => setPendingFilters({ ...pendingFilters, search: e.target.value })}
              onPressEnter={onApply}
              suffix={<Icon type="search" />}
            />
          </div>
        </div>
        <div css={styles.filterActions}>
          <Button onClick={onClear}>Clear Filters</Button>
          <Button type="primary" onClick={onApply}>Apply Filters</Button>
        </div>
      </div>

      <Table
        className="OperationsLogTable"
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        size="small"
        pagination={{ pageSize: 25, showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries` }}
        scroll={{ x: 1600 }}
      />

      <Modal
        title={modalEntry && modalEntry.id ? 'Edit Log Entry' : 'New Log Entry'}
        visible={modalOpen}
        onCancel={() => { setModalOpen(false); setModalEntry(null); }}
        footer={null}
        width={720}
        destroyOnClose
      >
        {modalOpen && (
          <EntryModal
            entry={modalEntry}
            onCancel={() => { setModalOpen(false); setModalEntry(null); }}
            onSave={onSave}
            saving={saving}
            licenses={licenses}
            licensesLoading={licensesLoading}
            orderIds={orderIds}
            orderIdsLoading={orderIdsLoading}
          />
        )}
      </Modal>

      <Modal
        title={(
          <span>
            <Icon type="warning" style={{ color: '#faad14', marginRight: 8 }} />
            Delete Log Entry
          </span>
        )}
        visible={!!confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onOk={onDelete}
        okText="Delete"
        okType="danger"
        confirmLoading={deleting}
      >
        <p>Are you sure you want to delete this Operations Log entry?</p>
        <p style={{ color: '#888', fontSize: 12 }}>This action cannot be undone.</p>
      </Modal>
    </div>
  );
};

export default OperationsLog;
